import { AppConfig } from 'AppConfig';
import * as ReportTypes from 'features/frontoffice/actions/types';
import { fetchData } from 'features/user/userActions';
import { getPendingIntent } from 'features/user/tenantSwitchSequence';
import * as UserTypes from 'features/user/userTypes';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import routePaths from 'routes/routePaths';
import { store } from 'store/store';
import LocalStore from 'utils/LocalStore';

const FrontOffice = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const HOME_URL = routePaths.frontOffice;
  const NEW_HOME_URL = `${HOME_URL}${AppConfig.FRONT_OFFICE_SUB_URL.NEW_HOME}`;
  const SMART_CARDS_URL = `${HOME_URL}${AppConfig.FRONT_OFFICE_SUB_URL.SMART_CARDS}`;
  
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);

  const openReportViewDialog = useSelector((state) => state.fo.openReportViewDialog);
  const { reportBaseUrl } = useSelector((state) => state.fo);

  const permissions = useSelector((state) => state.user.permissions);
  const currentApp = useSelector((state) => state.user.currentApp);

  const closeReportView = () => {
    store.dispatch({
      type: ReportTypes.CLOSE_REPORT_VIEWER_REQUEST
    });

    if (reportBaseUrl) {
      navigate(reportBaseUrl);
    }
  };

  // Redirect /bi?light-touch=true -> NEW_HOME_URL?light-touch=true
  useEffect(() => {
    const isOnHome = location.pathname === HOME_URL;
    if (isOnHome) {
      const params = new URLSearchParams(location.search);
      const lt = params.get('light-touch');
      // if (lt === 'true') {
      //   navigate(`${NEW_HOME_URL}?light-touch=true`, { replace: true });
      // }
      const tab = params.get('tab');
      const dest = new URLSearchParams();
      if (tab) dest.set('tab', tab);
      const search = dest.toString();
      navigate(HOME_URL + (search ? `?${search}` : ''), { replace: true });
    }
  }, [location.pathname, location.search, HOME_URL, NEW_HOME_URL, navigate]);

  useEffect(() => {
    const isOnNewHome = location.pathname === NEW_HOME_URL;
    if (isOnNewHome) {
      const params = new URLSearchParams(location.search);
      const lt = params.get('light-touch');
      // Redirect to HOME_URL if light-touch is missing or not 'true'
      if (lt !== 'true') {
        navigate(HOME_URL, { replace: true });
      }
    }
  }, [location.pathname, location.search, NEW_HOME_URL, HOME_URL, navigate]);

  //Redirect to HOME_URL if on Smart Cards and light-touch=false or missing
  useEffect(() => {
    const isOnSmartCards = location.pathname === SMART_CARDS_URL;
    if (isOnSmartCards) {
      const params = new URLSearchParams(location.search);
      navigate(SMART_CARDS_URL, { replace: true });
    }
  }, [location.pathname, location.search, SMART_CARDS_URL, HOME_URL, navigate]);

  useEffect(() => {
    const reportTenantCode = LocalStore.getReportTenantCode();
    const reportsRedirectUrl = LocalStore.getReportsRedirectUrl();

    // On a browser refresh, the JWT is restored before the rest of the user
    // context. Wait for its tenant claim to hydrate Redux rather than sending
    // the tenant-switch request with a null tenant.
    if (!currentTenantCd) {
      return;
    }

    if (!reportTenantCode && !reportsRedirectUrl) {
      // Compare against the pending switch, not committed state: while another app's
      // switch is in flight the committed app is stale, and returning here must win.
      if ((getPendingIntent().appCode ?? currentApp) !== AppConfig.APP_CODE_FO) {
        store.dispatch(fetchData(currentTenantCd, AppConfig.APP_CODE_FO, { explicitTenant: false }));
      }
    }
    /* always set the header menu type to FO when switching to FO app */
    store.dispatch({
      type: UserTypes.APP_SWITCHED,
      payload: AppConfig.HEADER_MENU_TYPE.FO
    });
  }, [currentApp, currentTenantCd]);

  useEffect(() => {
    if (!openReportViewDialog) {
      closeReportView();
    }
  }, [openReportViewDialog]); // eslint-disable-line react-hooks/exhaustive-deps    

  return null;
};

export default FrontOffice;
